import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  IconButton,
  Button,
  useTheme,
  TableContainer,
  Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

const OutOfStockReportDetails = ({ open, onClose, report }) => {
  const theme = useTheme();

  if (!report) return null;

  const handleDownloadCSV = () => {
    const headers = [
      'S.No',
      'Product',
      'SKU',
      'Quantity',
      'Minimum Qty',
      'MRP',
      'Unit',
    ];
    const rows = report.oosProducts.map((item, idx) => [
      idx + 1,
      item.name,
      item.skuId,
      item.quantity,
      item.minimumQty,
      item.mrp,
      `${item.measure} ${item.unit}`,
    ]);

    const csvContent =
      [headers, ...rows]
        .map(row =>
          row
            .map(field =>
              `"${String(field).replace(/"/g, '""')}"` // Escape quotes
            )
            .join(',')
        )
        .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    const timestamp = new Date().toISOString().slice(0, 19).replace(/[^0-9]/g, '');
    link.setAttribute(
      'download',
      `OutOfStockReport-${report.storeName}-${timestamp}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="out-of-stock-report-title"
      PaperProps={{
        sx: { borderRadius: theme.shape.borderRadius * 2 },
      }}
    >
      <DialogTitle
        id="out-of-stock-report-title"
        sx={{
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText, 
        }}
      >
        Out of Stock Details - {report.storeName}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.primary.contrastText,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          // 📝 DialogContent → Explicitly set to BLACK text
          color: theme.palette.common.black,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ mb: 1, color: theme.palette.text.secondary }}
        >
          Created At: {new Date(report.createdAt).toLocaleString()}
        </Typography>

        <Button
          variant="contained"
          startIcon={<FileDownloadIcon />}
          sx={{
            mb: 2,
            backgroundColor: theme.palette.success.main,
            color: theme.palette.common.white,
            '&:hover': {
              backgroundColor: theme.palette.success.dark,
            },
          }}
          onClick={handleDownloadCSV}
        >
          Download CSV
        </Button>

        <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
          <Table>
            <TableHead
              sx={{
                backgroundColor: theme.palette.primary.main, 
                '& th': {
                  color: theme.palette.primary.contrastText, 
                },
              }}
            >
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Product</TableCell>
                <TableCell>SKU</TableCell>
                <TableCell>Qty</TableCell>
                <TableCell>Min Qty</TableCell>
                <TableCell>MRP</TableCell>
                <TableCell>Unit</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {report.oosProducts && report.oosProducts.length > 0 ? (
                report.oosProducts.map((item, idx) => (
                  <TableRow key={item.itemId || idx}>
                    {/* 🏁 TableBody → Explicitly set to BLACK text */}
                    <TableCell sx={{ color: theme.palette.common.black }}>{idx + 1}</TableCell>
                    <TableCell sx={{ color: theme.palette.common.black }}>{item.name}</TableCell>
                    <TableCell sx={{ color: theme.palette.common.black }}>{item.skuId}</TableCell>
                    <TableCell sx={{ color: theme.palette.common.black }}>{item.quantity}</TableCell>
                    <TableCell sx={{ color: theme.palette.common.black }}>{item.minimumQty}</TableCell>
                    <TableCell sx={{ color: theme.palette.common.black }}>{item.mrp}</TableCell>
                    <TableCell sx={{ color: theme.palette.common.black }}>
                      {item.measure} {item.unit}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ color: theme.palette.text.secondary }}>
                    No out-of-stock products found for this report.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
};

export default OutOfStockReportDetails;
